import * as React from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { fallback } from "@tanstack/zod-adapter";
import { Loader2Icon } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";
import { z } from "zod";

import { checkIsPaidPlan } from "@degaus/shared/plans";

import { Button } from "@/components/ui/Button";
import { GettingStartedStep } from "@/features/wonda/components/GettingStartedStep";
import { checkIsConnectorUser } from "@/features/wonda/connectorAudience";
import { useUser } from "@/hooks/useUser";
import { api } from "@/trpc/react";

export const guideStepKeys = ["setup", "capabilities", "complete"] as const;
export type GuideStepKey = (typeof guideStepKeys)[number];

export const guideSearchSchema = z.object({
  step: fallback(z.enum(guideStepKeys).optional(), undefined),
});

// The onboarding walkthrough, shared by /onboarding/guide (default) and
// /onboarding/guide/linkedin (the LinkedIn-path variant). Step navigation uses
// `to: "."` so it stays on whichever route rendered it.
export function GuideExperience({
  variant = "default",
}: {
  variant?: "default" | "linkedin";
}) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const posthog = usePostHog();
  const { user } = useUser();

  const currentStepKey: GuideStepKey = search.step ?? "setup";

  const handleStepChange = React.useCallback(
    (nextStepKey: string) => {
      void navigate({
        to: ".",
        search: { step: nextStepKey as GuideStepKey },
        replace: true,
      });
    },
    [navigate],
  );

  const { data: welcomeAnswers } = api.account.getWelcomeAnswers.useQuery();
  const { data: currentPlan } = api.payments.getCurrentAccountPlan.useQuery(
    undefined,
    { staleTime: 60_000 },
  );
  // Org membership drives the back-nav paywall skip (handleBack).
  const { data: orgList } = api.organization.list.useQuery(undefined, {
    staleTime: 60_000,
  });
  const isOrgMember = (orgList?.length ?? 0) > 0;

  const planCode = currentPlan?.planCode ?? null;

  const tools = welcomeAnswers?.tools ?? [];
  const isTechnicalAgent =
    tools.includes("claude-code") || tools.includes("codex");
  const isInstallPrereqs = !isTechnicalAgent;
  // Everyone the questionnaire puts on the connector path, not just Cowork:
  // a ChatGPT user drives the same relay and the same browser logins, so
  // leaving them out of this gate would hand them the celebration screen with
  // nothing connected. See connectorAudience for why Codex is excluded.
  const isConnectorUser = checkIsConnectorUser(tools);

  // Copy gate on the completion step (not a hard block): a connector user whose
  // relay is online but who hasn't logged into any platform yet gets an
  // "almost there" screen instead of the celebration. CLI-path users never see
  // it — the relay/cookie-backup signal doesn't apply to them.
  const { data: relayStatus, isLoading: isRelayStatusLoading } =
    api.account.getRelayOnboardingStatus.useQuery(undefined, {
      staleTime: 30_000,
      enabled: isConnectorUser,
    });
  const { data: connectedPlatforms, isLoading: isPlatformsLoading } =
    api.account.listConnectedPlatforms.useQuery(undefined, {
      staleTime: 30_000,
      enabled: isConnectorUser,
    });
  const needsPlatformConnection =
    isConnectorUser &&
    relayStatus?.connected === true &&
    connectedPlatforms !== undefined &&
    connectedPlatforms.length === 0;
  // Don't flash the celebration while the gate signals are still loading
  // (fail-open: a query error falls through to the normal completion).
  const isGateLoading =
    isConnectorUser && (isRelayStatusLoading || isPlatformsLoading);

  const completeWelcome = api.account.completeWelcome.useMutation();

  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const handleCopy = React.useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  }, []);

  const handleComplete = React.useCallback(async () => {
    posthog.capture("welcome guide completed", { variant });
    try {
      await completeWelcome.mutateAsync({
        tools: welcomeAnswers?.tools ?? [],
        platforms: welcomeAnswers?.platforms ?? [],
      });
      await user?.reload();
      void navigate({ to: "/account", replace: true });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to complete onboarding",
      );
    }
  }, [completeWelcome, welcomeAnswers, posthog, user, navigate, variant]);

  const handleBack = React.useCallback(() => {
    // Paid users and org members skip the paywall step on the way forward, so they should also skip it going back.
    const skipPaywall = checkIsPaidPlan(planCode) || isOrgMember;
    void navigate({
      to: skipPaywall ? "/onboarding/platforms" : "/onboarding/paywall",
    });
  }, [navigate, planCode, isOrgMember]);

  if (currentStepKey === "complete" && isGateLoading) {
    return (
      <div className="flex min-h-[calc(100dvh-var(--header-height,80px))] items-center justify-center px-6">
        <Loader2Icon
          className="size-6 animate-spin"
          style={{ color: "var(--cli-text-muted)" }}
        />
      </div>
    );
  }

  if (currentStepKey === "complete" && needsPlatformConnection) {
    return (
      <div className="flex min-h-[calc(100dvh-var(--header-height,80px))] items-center justify-center px-6">
        <section className="w-full max-w-lg space-y-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-lg font-semibold text-[var(--cli-text)]">
              Almost there: connect one account
            </p>
            <p className="text-sm text-[var(--cli-text-muted)]">
              Wonda is set up, but no social accounts are connected yet. Ask
              your AI app to log you into{" "}
              {variant === "linkedin" ? "LinkedIn" : "LinkedIn, X, or Reddit"}{" "}
              using Wonda before you wrap up.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="default"
              onClick={() => handleStepChange("capabilities")}
            >
              ← Previous
            </Button>
            <Button
              variant="steward"
              size="default"
              disabled={completeWelcome.isPending}
              onClick={handleComplete}
            >
              {completeWelcome.isPending
                ? "Loading..."
                : "Continue to your account →"}
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <GettingStartedStep
      variant={variant}
      tools={tools}
      copiedField={copiedField}
      isPending={completeWelcome.isPending}
      isInstallPrereqs={isInstallPrereqs}
      currentStepKey={currentStepKey}
      onStepChange={handleStepChange}
      onCopy={handleCopy}
      onComplete={handleComplete}
      onBack={handleBack}
    />
  );
}
